/* ============================================================
   MOTOVILE — Contact page (progressive enhancement only)

   What this script does:
     1. After a submit, scrolls the success/error note into view and
        moves focus to it (native-email mode).
     2. On an invalid submit, scrolls the first invalid field into view.
     3. Anti-spam (when enabled on the section):
          a) Honeypot field — if a hidden field is non-empty,
             silently reject the submit.
          b) Time check — submits in < 2 seconds get silently rejected.
     4. Smart-routing variant:
          - Conditional fields revealed by `data-cp-show-if` matching
            the selected reason. Hidden inputs are `disabled` so HTML5
            required validation ignores them while invisible.
          - Reason-aware pledge banner (response-time expectation) +
            reason-aware textarea placeholder/hint.
     5. File upload UX (drop zone + selected-file chips).
     6. On VALID submit:
          - Builds a CANONICAL payload (every known key is always
            present, empty for fields absent from the active variant)
            so n8n receives a consistent shape regardless of variant.
          - Fires a fire-and-forget POST to the webhook. Uses FormData
            (multipart) when files are present; URLSearchParams
            otherwise.
     7. If "Send Shopify native email" is OFF, preventDefaults the
        submit so /contact is never hit; shows inline success message.
     8. Re-inits cleanly inside the Shopify theme editor.
   ============================================================ */
(function () {
  'use strict';

  var MIN_FILL_MS = 2000;

  // Canonical keys ALWAYS included in the JSON payload (empty when the
  // active variant doesn't render the field). Keeps n8n flow simple.
  var CANONICAL_KEYS = [
    'name', 'first_name', 'last_name',
    'email',
    'phone', 'phone_number', 'whatsapp_number',
    'message', 'body',
    'reason',
    'order_number',
    'vehicle_details',
    'role_of_interest',
    'company', 'country',
    'inquiry_type', 'estimated_monthly_volume',
    'preferred_contact_method',
    'attachments'
  ];

  // Reason → pledge text (response-time expectation) + textarea placeholder
  var REASON_COPY = {
    general:   { pledge: '',                                                                                  msg: '' },
    fitment:   { pledge: 'We confirm fitment within 4 hours during business hours.',                          msg: 'Tell us the part number (or product link) and any photos of where it needs to fit.' },
    order:     { pledge: 'We pull order status within 2 hours and reply with tracking + ETA.',                msg: 'Order number above. Add anything specific (delivery address change, ETA question, missing item)…' },
    return:    { pledge: 'We respond within 4 hours with a return label or exchange options.',                msg: 'Tell us what arrived wrong + photos help us approve a refund or exchange faster.' },
    wholesale: { pledge: 'Our wholesale team responds within 24 hours with pricing + terms.',                 msg: 'Tell us roughly what categories you stock, your monthly volume, and your target markets…' },
    career:    { pledge: 'We review applications within 5 business days. Upload your CV below.',              msg: 'Tell us about yourself, why Motovile, and any motorsports experience…' },
    other:     { pledge: '',                                                                                  msg: '' }
  };

  function safeScroll(el) {
    if (!el) return;
    try { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); }
    catch (e) { el.scrollIntoView(); }
  }

  function snakeCase(s) {
    return String(s || '').trim().toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
  }

  /**
   * Build the JSON metadata payload (NOT used as the POST body — used as
   * the `_payload_json` field for consumers that prefer parsed JSON).
   * Always emits every CANONICAL_KEYS field; missing fields are "".
   */
  function buildMetadataPayload(form, root) {
    var payload = {};
    for (var i = 0; i < CANONICAL_KEYS.length; i++) payload[CANONICAL_KEYS[i]] = '';

    payload.submitted_at = new Date().toISOString();
    payload.page_url     = window.location.href;
    payload.referrer     = document.referrer || '';
    payload.user_agent   = navigator.userAgent || '';
    payload.shop_domain  = (window.Shopify && window.Shopify.shop) || '';
    payload.form_variant = (root && root.getAttribute('data-cp-variant')) || '';

    var fileNames = [];

    try {
      var fd = new FormData(form);
      var entries = fd.entries();
      while (true) {
        var step = entries.next();
        if (step.done) break;
        var rawKey = step.value[0];
        var val    = step.value[1];

        // Skip honeypot + Shopify internal tokens.
        if (rawKey === 'contact[website_url]') continue;
        if (rawKey === 'form_type' || rawKey === 'utf8') continue;

        // Collect file names separately (the binaries are sent in the body).
        if (val && typeof val === 'object' && 'name' in val && 'size' in val) {
          if (val.size > 0) fileNames.push(val.name);
          continue;
        }

        var keyMatch = rawKey.match(/^contact\[(.+)\]$/);
        var clean    = snakeCase(keyMatch ? keyMatch[1] : rawKey);
        if (!clean) continue;
        payload[clean] = (val == null ? '' : String(val));
      }
    } catch (e) { /* FormData iter not supported — payload stays canonical-only */ }

    // Mirror so consumers don't need to guess which alias is used.
    payload.phone        = payload.phone || payload.phone_number || payload.whatsapp_number || '';
    payload.message      = payload.message || payload.body || '';
    payload.body         = payload.body || payload.message || '';
    payload.attachments  = fileNames.join(', ');
    payload.file_names   = fileNames;
    payload.file_count   = fileNames.length;

    return payload;
  }

  function fireWebhook(form, url, root, hasFiles) {
    if (!url || !/^https?:\/\//i.test(url)) return;
    var payload = buildMetadataPayload(form, root);

    var fetchOpts = {
      method: 'POST',
      mode: 'no-cors',
      credentials: 'omit',
      cache: 'no-store'
    };

    if (hasFiles) {
      // Multipart — required for file binaries. Drop the honeypot field
      // from the body so n8n doesn't see it, then attach the JSON copy.
      var fd = new FormData(form);
      fd.delete('contact[website_url]');
      try { fd.append('_payload_json', JSON.stringify(payload)); } catch (e) {}
      fetchOpts.body = fd;
      // keepalive caps at ~64KB; files easily exceed it. We only need
      // keepalive when the form is also going to navigate (native email
      // mode). When suppress mode is on (default), there's no navigation
      // so we can leave keepalive off and not hit the cap.
    } else {
      // Compact urlencoded body — smaller and faster than multipart.
      var params = new URLSearchParams();
      Object.keys(payload).forEach(function (k) {
        var v = payload[k];
        if (v == null) v = '';
        if (typeof v === 'object') v = JSON.stringify(v);
        params.append(k, String(v));
      });
      try { params.append('_payload_json', JSON.stringify(payload)); } catch (e) {}
      fetchOpts.body = params;
      fetchOpts.keepalive = true;
    }

    try {
      fetch(url, fetchOpts).catch(function () { /* fire-and-forget */ });
    } catch (e) { /* no-op */ }
  }

  function showInlineSuccess(form) {
    var tpl = form.querySelector('[data-cp-success-template]');
    if (!tpl || !tpl.content) {
      form.innerHTML = '<p class="cp-form__success" role="status">Thanks for reaching out — we’ll get back to you shortly.</p>';
      return form;
    }
    var container = document.createElement('div');
    container.className = 'cp-form-success-container';
    container.appendChild(tpl.content.cloneNode(true));
    form.parentNode.replaceChild(container, form);
    safeScroll(container);
    window.setTimeout(function () {
      var msg = container.querySelector('.cp-form__success');
      if (msg && typeof msg.focus === 'function') msg.focus();
    }, 100);
    return container;
  }

  function isHoneypotTripped(form) {
    var hp = form.querySelector('[data-cp-hp]');
    return hp && hp.value && hp.value.length > 0;
  }

  function isTooFast(form) {
    var ts = form.querySelector('[data-cp-ts]');
    if (!ts || !ts.value) return false;
    var t = parseInt(ts.value, 10);
    if (isNaN(t)) return false;
    return (Date.now() - t) < MIN_FILL_MS;
  }

  /** Smart-routing conditional fields + reason-aware pledge & message. */
  function wireConditionalFields(form) {
    var trigger = form.querySelector('[data-cp-reason]');
    if (!trigger) return;

    var blocks  = form.querySelectorAll('[data-cp-show-if]');
    var pledge  = form.querySelector('[data-cp-pledge]');
    var pledgeText = form.querySelector('[data-cp-pledge-text]');
    var msgArea = form.querySelector('[data-cp-msg]');

    // Remember which conditional inputs were originally required so we can
    // restore the attribute when the field becomes visible again.
    for (var b = 0; b < blocks.length; b++) {
      var inputs = blocks[b].querySelectorAll('input, select, textarea');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].required) inputs[i].setAttribute('data-cp-was-required', 'true');
      }
    }

    function apply() {
      var val = trigger.value || '';

      // Toggle each conditional block.
      for (var k = 0; k < blocks.length; k++) {
        var block = blocks[k];
        var triggers = (block.getAttribute('data-cp-show-if') || '').split(',');
        var show = triggers.indexOf(val) !== -1;
        block.hidden = !show;
        var fields = block.querySelectorAll('input, select, textarea');
        for (var f = 0; f < fields.length; f++) {
          var fld = fields[f];
          fld.disabled = !show;
          if (show && fld.getAttribute('data-cp-was-required') === 'true') {
            fld.required = true;
          }
        }
      }

      // Pledge banner.
      var copy = REASON_COPY[val] || REASON_COPY.general;
      if (pledge) {
        if (copy.pledge) {
          if (pledgeText) pledgeText.textContent = copy.pledge;
          pledge.hidden = false;
        } else {
          pledge.hidden = true;
        }
      }

      // Reason-aware textarea placeholder — works as a hint inside the
      // empty field. When the user starts typing the placeholder hides
      // naturally; if reason changes mid-type, the placeholder updates
      // and reappears the next time the field is empty.
      if (msgArea) {
        msgArea.setAttribute('placeholder', copy.msg || ' ');
      }
    }

    trigger.addEventListener('change', apply);
    apply();
  }

  /** File-input UX: drop-zone styling + selected-file chips. */
  function wireFileDrop(form) {
    var drops = form.querySelectorAll('.cp-file-drop');
    if (!drops.length) return;

    for (var i = 0; i < drops.length; i++) {
      (function (drop) {
        var input = drop.querySelector('.cp-file-input');
        var list  = drop.querySelector('[data-cp-files-list]');
        if (!input || !list) return;

        function render() {
          list.innerHTML = '';
          var files = input.files;
          if (!files || !files.length) return;
          for (var i = 0; i < files.length; i++) {
            var f = files[i];
            var chip = document.createElement('span');
            chip.className = 'cp-file-chip';
            var name = document.createElement('span');
            name.className = 'cp-file-chip__name';
            name.textContent = f.name;
            var size = document.createElement('span');
            size.className = 'cp-file-chip__size';
            size.textContent = '· ' + Math.max(1, Math.round(f.size / 1024)) + ' KB';
            chip.appendChild(name);
            chip.appendChild(size);
            list.appendChild(chip);
          }
        }

        input.addEventListener('change', render);

        ['dragenter', 'dragover'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) {
            e.preventDefault();
            drop.classList.add('is-dragover');
          });
        });
        ['dragleave', 'drop'].forEach(function (ev) {
          drop.addEventListener(ev, function (e) {
            e.preventDefault();
            drop.classList.remove('is-dragover');
          });
        });
        drop.addEventListener('drop', function (e) {
          if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length) {
            input.files = e.dataTransfer.files;
            render();
          }
        });
      })(drops[i]);
    }
  }

  function initContactPage(root) {
    if (!root || root.dataset.cpInit === 'true') return;
    root.dataset.cpInit = 'true';

    var suppressNative = root.getAttribute('data-cp-suppress-native') === 'true';
    var spamProtect    = root.getAttribute('data-cp-spam-protect')    === 'true';
    var webhookUrl     = root.getAttribute('data-cp-webhook') || '';

    var note = root.querySelector('[data-cp-success], .cp-form__success, .cp-form__error');
    if (note && !note.hasAttribute('data-cp-success-template')) {
      safeScroll(note);
      window.setTimeout(function () {
        if (typeof note.focus === 'function') note.focus();
      }, 300);
    }

    var form = root.querySelector('.cp-form');
    if (!form) return;

    if (spamProtect) {
      var ts = form.querySelector('[data-cp-ts]');
      if (ts && !ts.value) ts.value = String(Date.now());
    }

    wireConditionalFields(form);
    wireFileDrop(form);

    form.addEventListener('invalid', function (event) {
      if (!event.target || form.dataset.cpInvalidHandled === 'true') return;
      form.dataset.cpInvalidHandled = 'true';
      safeScroll(event.target);
      window.setTimeout(function () { form.dataset.cpInvalidHandled = ''; }, 0);
    }, true);

    form.addEventListener('submit', function (event) {
      if (spamProtect && isHoneypotTripped(form)) { event.preventDefault(); return; }
      if (spamProtect && isTooFast(form))         { event.preventDefault(); return; }

      // Did the active variant produce a file input with selected files?
      var hasFiles = false;
      var fileInputs = form.querySelectorAll('input[type="file"]:not([disabled])');
      for (var i = 0; i < fileInputs.length; i++) {
        if (fileInputs[i].files && fileInputs[i].files.length) { hasFiles = true; break; }
      }

      fireWebhook(form, webhookUrl, root, hasFiles);

      if (suppressNative) {
        event.preventDefault();
        showInlineSuccess(form);
        return;
      }

      var btn = form.querySelector('.cp-submit');
      if (btn && !btn.disabled) {
        btn.setAttribute('aria-busy', 'true');
        window.setTimeout(function () { btn.disabled = true; }, 0);
      }
    });
  }

  function initAll() {
    var nodes = document.querySelectorAll('.contact-page');
    for (var i = 0; i < nodes.length; i++) initContactPage(nodes[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector ? event.target.querySelector('.contact-page') : null;
    if (root) {
      root.dataset.cpInit = '';
      initContactPage(root);
    }
  });
})();
